export interface SummaryRecipient {
  /** Display name; falls back to the email when empty. */
  name: string
  email: string
}

/**
 * Collapse a recipient list into the Gmail-style "Name (email), … +N more"
 * summary: the first `max` recipients rendered as text, and the count of
 * everyone past that. A recipient with no name shows just its email.
 *
 * Pure so it can be unit-tested without rendering (this repo's vitest runs in a
 * node environment with no DOM).
 */
export function formatReplyRecipients(
  recipients: SummaryRecipient[],
  max = 2
): { text: string; overflow: number } {
  const shown = recipients.slice(0, max)
  return {
    text: shown
      .map((r) => (r.name ? `${r.name} (${r.email})` : r.email))
      .join(', '),
    overflow: Math.max(0, recipients.length - shown.length),
  }
}

/**
 * Gmail-style recipient summary for the reply composer header: lists the people
 * a reply will go to as "Name (email)", collapsing everything past the first
 * `max` into a "+N more" chip. When `onExpand` is set the whole row is a button
 * so clicking it can reveal the editable Cc/Bcc rows.
 *
 * Built for the campaign Reply-All composer (FIR-7804): that header used to show
 * the thread subject, which hid the To recipient entirely, so the sender of the
 * reply couldn't see who they were responding to.
 */
export function InlineReplyRecipients({
  recipients,
  max = 2,
  onExpand,
}: {
  recipients: SummaryRecipient[]
  max?: number
  onExpand?: () => void
}) {
  const { text, overflow } = formatReplyRecipients(recipients, max)

  // The name text inherits `color` from the wrapper so the button's hover state
  // (below) actually reaches it; the "+N more" chip keeps its own muted color.
  const content = (
    <>
      <span className="truncate text-sm font-medium">{text}</span>
      {overflow > 0 && (
        <span className="flex-shrink-0 rounded border border-neutral-300 px-1.5 py-0.5 text-xs text-neutral-600">
          +{overflow} more
        </span>
      )}
    </>
  )

  if (!onExpand) {
    return (
      <div className="flex min-w-0 items-center gap-1.5 text-neutral-800">
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label="Edit recipients"
      className="flex min-w-0 items-center gap-1.5 text-left text-neutral-800 hover:text-neutral-900"
    >
      {content}
    </button>
  )
}
